import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { Auth, API, Hub } from 'aws-amplify';
import {
  Authenticator,
  Greetings,
  SignUp,
  ForgotPassword,
  SignIn,
  VerifyContact,
  Loading,
} from 'aws-amplify-react';
import { useLocation, useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import partnerConfig from '../../partner-aws-exports';
import staffConfig from '../../staff-aws-exports';
import { devLog } from '../../utils';
import CustomSignIn from './CustomSignIn/CustomSignIn';
import { fetchSsoCredsAction, setLogoutAction } from '../../actions/loginAction';
import { revertObscureString, getAppVersion } from '../../utils/helpers';
import { getAppConfig } from '../../constants/appConfig';
import { saveToSessionStorage } from '../../utils/apiHelpers';
import { isEmpty } from '../../utils/baseHelpers';
import {
  affiliatedRestaurants as affiliatedRestaurantsQuery,
  restIdBySettlementId,
  userById,
} from '../../graphql/queries';
import App from '../../App';

const AppWithAuth = ({ ssoCreds, fetchSsoCreds, setLogout }) => {
  const history = useHistory();
  const partnerSession = localStorage.getItem('mode') === 'partner';
  const staffSession = localStorage.getItem('mode') === 'staff';

  const location = useLocation();
  const staffLogin = location?.pathname === '/staff';

  const params = new URLSearchParams(window.location.search);
  const ssoToken = params.get('ssoToken');

  const [authSignedIn, setAuthSignedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // This is used to prevent redirects cancelling other redirects
  const redirecting = useRef(false);
  const hubListenerAttached = useRef(false);
  const staffSignOutRef = useRef(() => {});

  const staffSignOut = () => {
    redirecting.current = true;
    const searchParams = new URLSearchParams(window.location.search);
    window.location.href = `${import.meta.env.VITE_LOGIN_URL}?auth=signOut&callback_url=${
      window.location.origin
    }/staff${searchParams.get('restId') ? `&restId=${searchParams.get('restId')}` : ''}`;
  };

  staffSignOutRef.current = staffSignOut;

  const onSsoFailure = () => {
    devLog('info', 'sso failure');
    params.delete('ssoToken');
    const paramsString = params.toString();
    redirecting.current = true;
    window.history.replaceState(
      {},
      document.title,
      `${!isEmpty(paramsString) ? `?${paramsString}` : '/'}`,
    );

    window.location.reload();
  };

  useEffect(() => {
    if (!hubListenerAttached.current) {
      hubListenerAttached.current = true;
      Hub.listen('auth', ({ payload: { event } }) => {
        switch (event) {
          case 'signIn':
            devLog('debug', 'signIn');
            setAuthSignedIn(true);
            break;
          case 'signOut':
            devLog('debug', 'signOut');
            {
              const mode = localStorage.getItem('mode');
              localStorage.removeItem('UserId');
              if (mode === 'staff') {
                localStorage.removeItem('mode');
                staffSignOutRef.current();
              } else {
                localStorage.removeItem('mode');
                setAuthSignedIn(false);
                setAuthLoading(false);
              }
            }
            break;
          case 'customOAuthState':
            break;
          case 'signIn_failure':
            break;
          default:
            break;
        }
      });
    }
  }, []);

  useEffect(() => {
    if (params.get('origin') === 'hub' && partnerSession) {
      // localStorage.clear(); // TODO don't want to wipe all local storage. Check what Obee FOH is doing
    }

    if (!ssoToken) {
      return undefined;
    }

    if (staffLogin) {
      devLog(
        'info',
        '[partnerSsoGate]',
        'staff path: calling fetchSsoCreds (partner gate skipped)',
      );
      try {
        fetchSsoCreds(ssoToken, true);
      } catch (e) {
        console.error(e);
        onSsoFailure();
      }
      return undefined;
    }

    let cancelled = false;

    const runPartnerSsoGate = async () => {
      const gateLog = (msg, extra) => devLog('info', '[partnerSsoGate]', msg, extra ?? '');
      const searchParams = new URLSearchParams(window.location.search);
      gateLog('started', {
        pathname: window.location.pathname,
        hasSsoToken: true,
        hasRestIdParam: Boolean(searchParams.get('restId')),
        hasSettlementParam: Boolean(searchParams.get('settlement')),
      });

      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        if (cancelled) {
          gateLog('aborted after Auth (unmount)');
          return;
        }

        const isPartner =
          cognitoUser?.pool?.userPoolId === import.meta.env.VITE_AWS_PARTNER_USER_POOLS_ID;
        if (!isPartner) {
          gateLog('fallback fetchSsoCreds: signed-in user is not partner pool');
          fetchSsoCreds(ssoToken, false);
          return;
        }

        const storedUserId = localStorage.getItem('UserId')?.trim() || null;
        const cognitoObjectId = cognitoUser?.attributes?.['custom:objectId']?.trim() || null;

        const partnerHeaders = (uid, restIdForHeader) => ({
          'user-id': uid,
          'user-type': 'partner',
          ...(restIdForHeader ? { 'rest-id': restIdForHeader } : {}),
          'app-version': getAppVersion(),
          'ec-country': getAppConfig().ecCountryCode,
        });

        const fetchUserVenuesForGate = async (userId) => {
          const userResponse = await API.graphql(
            {
              query: userById,
              variables: { userId },
              authMode: 'AMAZON_COGNITO_USER_POOLS',
            },
            partnerHeaders(userId),
          );
          const rawRestaurants = userResponse?.data?.userById?.restaurants;
          const venues = rawRestaurants?.length ? rawRestaurants.map((r) => JSON.parse(r)) : [];
          const canonical = userResponse?.data?.userById?.objectId?.trim() || userId;
          return { venues, canonical };
        };

        let userObjectId;
        let partnerRestaurants = [];
        let canonicalUserId;

        try {
          if (
            storedUserId &&
            cognitoObjectId &&
            storedUserId.toUpperCase() !== cognitoObjectId.toUpperCase()
          ) {
            const [fromStored, fromCognito] = await Promise.all([
              fetchUserVenuesForGate(storedUserId).catch(() => ({
                venues: [],
                canonical: storedUserId,
              })),
              fetchUserVenuesForGate(cognitoObjectId).catch(() => ({
                venues: [],
                canonical: cognitoObjectId,
              })),
            ]);
            const nS = fromStored.venues.length;
            const nC = fromCognito.venues.length;
            const preferCognito = nC > nS || (nC >= 2 && nS < 2);
            if (preferCognito) {
              userObjectId = cognitoObjectId;
              partnerRestaurants = fromCognito.venues;
              canonicalUserId = fromCognito.canonical;
              gateLog('UserId vs Cognito: using Cognito user (clearing stale UserId)', {
                storedUserId,
                cognitoObjectId,
                venueCountStored: nS,
                venueCountCognito: nC,
              });
              localStorage.removeItem('UserId');
            } else {
              userObjectId = storedUserId;
              partnerRestaurants = fromStored.venues;
              canonicalUserId = fromStored.canonical;
              gateLog('UserId vs Cognito: keeping UserId override', {
                storedUserId,
                cognitoObjectId,
                venueCountStored: nS,
                venueCountCognito: nC,
              });
            }
          } else {
            userObjectId = cognitoObjectId || storedUserId;
            if (!userObjectId) {
              gateLog('fallback fetchSsoCreds: no user objectId on Cognito user');
              fetchSsoCreds(ssoToken, false);
              return;
            }
            const row = await fetchUserVenuesForGate(userObjectId);
            partnerRestaurants = row.venues;
            canonicalUserId = row.canonical;
          }
        } catch (err) {
          devLog('error', 'userById (partner SSO gate)', err);
          gateLog('fallback fetchSsoCreds: userById failed');
          fetchSsoCreds(ssoToken, false);
          return;
        }

        if (!userObjectId) {
          gateLog('fallback fetchSsoCreds: no user objectId on Cognito user');
          fetchSsoCreds(ssoToken, false);
          return;
        }

        let targetRestId = searchParams.get('restId');

        if (targetRestId) {
          gateLog('resolved restId from URL query param', { restId: targetRestId });
        } else {
          const settlementId = searchParams.get('settlement');
          if (settlementId) {
            gateLog('resolving restId via restIdBySettlementId', { settlementId });
            try {
              const restIdResponse = await API.graphql(
                {
                  query: restIdBySettlementId,
                  variables: { settlementId },
                  authMode: 'AMAZON_COGNITO_USER_POOLS',
                },
                partnerHeaders(userObjectId),
              );
              targetRestId = restIdResponse?.data?.restIdBySettlementId?.restId ?? null;
              gateLog('restIdBySettlementId result', { targetRestId });
            } catch (err) {
              devLog('error', 'restIdBySettlementId', err);
              targetRestId = null;
            }
          } else {
            gateLog('no restId or settlement query param');
          }
        }

        if (cancelled) {
          gateLog('aborted before access check (unmount)');
          return;
        }

        if (!targetRestId) {
          gateLog('fallback fetchSsoCreds: could not resolve target restId');
          fetchSsoCreds(ssoToken, false);
          return;
        }

        gateLog('userById for access check', {
          userObjectId,
          canonicalUserId,
          venueCount: partnerRestaurants.length,
          targetRestId,
        });

        if (cancelled) {
          gateLog('aborted after userById (unmount)');
          return;
        }

        const normalizedTarget = targetRestId.toUpperCase();
        const matchedRestaurant = partnerRestaurants.find(
          (r) => r?.objectId && r.objectId.toUpperCase() === normalizedTarget,
        );
        const hasAccess = Boolean(matchedRestaurant);
        const canonicalRestId = matchedRestaurant?.objectId ?? targetRestId;

        if (!hasAccess) {
          gateLog('fallback fetchSsoCreds: user has no access to target restId', { targetRestId });
          fetchSsoCreds(ssoToken, false);
          return;
        }

        const uniqueVenueKey = (id) => String(id || '').toUpperCase();
        // Group can be: multiple rows on userById, OR one row + siblings from affiliatedRestaurants
        // (same model as RestaurantSwitcher merge).
        let hasMultipleVenueAccess = partnerRestaurants.length >= 2;

        if (!hasMultipleVenueAccess && canonicalRestId) {
          const uniqueVenueIds = new Set(
            partnerRestaurants.filter((r) => r?.objectId).map((r) => uniqueVenueKey(r.objectId)),
          );
          uniqueVenueIds.add(uniqueVenueKey(canonicalRestId));
          try {
            const affResponse = await API.graphql(
              {
                query: affiliatedRestaurantsQuery,
                variables: { restId: canonicalRestId },
                authMode: 'AMAZON_COGNITO_USER_POOLS',
              },
              partnerHeaders(userObjectId, canonicalRestId),
            );
            const affList = affResponse?.data?.affiliatedRestaurants || [];
            affList.forEach((r) => {
              if (r?.objectId) uniqueVenueIds.add(uniqueVenueKey(r.objectId));
            });
            hasMultipleVenueAccess = uniqueVenueIds.size >= 2;
            gateLog('multi-venue check incl. affiliates', {
              userByIdVenueCount: partnerRestaurants.length,
              affiliateCount: affList.length,
              uniqueVenuesForGate: uniqueVenueIds.size,
            });
          } catch (affErr) {
            devLog('warn', '[partnerSsoGate] affiliatedRestaurants', affErr);
          }
        }

        // Only skip SSO refresh for multi-venue (group) partners — keeps multi-access session.
        // Single-venue partners keep the existing fetchSsoCreds path.
        if (!hasMultipleVenueAccess) {
          gateLog('fallback fetchSsoCreds: single-venue partner, not group SSO skip', {
            venueCount: partnerRestaurants.length,
          });
          fetchSsoCreds(ssoToken, false);
          return;
        }

        saveToSessionStorage(`selectedRestaurant:${canonicalUserId}`, canonicalRestId);
        localStorage.setItem(`lastUsedRestaurant:${canonicalUserId}`, canonicalRestId);

        searchParams.delete('ssoToken');
        searchParams.delete('restId');
        const paramsString = searchParams.toString();
        redirecting.current = true;

        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${!isEmpty(paramsString) ? `?${paramsString}` : ''}`,
        );

        setAuthSignedIn(true);
        setAuthLoading(false);
        gateLog('SKIP SSO — session kept, venue selected', {
          userObjectId,
          canonicalUserId,
          targetRestId,
          canonicalRestId,
          remainingQuery: paramsString || '(none)',
        });
      } catch (e) {
        if (!cancelled) {
          devLog(
            'warn',
            '[partnerSsoGate]',
            'not signed in or Auth error → fetchSsoCreds',
            e?.message ?? e,
          );
          fetchSsoCreds(ssoToken, false);
        }
      }
    };

    runPartnerSsoGate();

    return () => {
      cancelled = true;
    };
  }, [fetchSsoCreds, ssoToken, staffLogin]);

  // On failure, wipe sso token
  useEffect(() => {
    if (ssoCreds?.error && ssoToken) {
      onSsoFailure();
    }
  }, [ssoCreds?.error, ssoToken]);

  const login = async (email, password) => {
    try {
      const user = await Auth.signIn(email.trim(), password);
      params.delete('ssoToken');
      devLog('success', 'user', user);
    } catch (error) {
      devLog('error', 'user', error);
      setLogout();
    }
  };

  // When sso login is complete
  const oldSsoSuccess = useRef(ssoCreds?.success); // Prevents auto-login if the user hasn't refreshed the page yet
  useEffect(() => {
    if (!oldSsoSuccess.current && ssoCreds?.success) {
      const creds = revertObscureString(ssoCreds?.data).split(';');

      // User login. Get creds then log in for that user
      if (!staffLogin) {
        // Assuming we get the email and password
        const email = creds?.[0];
        const password = creds?.[1];
        const userId = creds?.[2]; // Optional userId in the response if it was a magic link login

        // If they used the magic link login, store the userId they want to be
        if (userId && userId !== 'null') {
          localStorage.setItem(`UserId`, userId);
        } else {
          // Otherwise clear it so it doesn't try connecting the wrong user!
          localStorage.removeItem(`UserId`);
        }

        // failsafe if currently logged in as staff
        if (localStorage.getItem('mode') === 'staff') {
          console.log('have sso but removing staff mode anyway');
          localStorage.removeItem('mode');
        }

        // Remove the ssoToken from the url
        params.delete('ssoToken');
        const paramsString = params.toString();
        redirecting.current = true;

        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}${!isEmpty(paramsString) ? `?${paramsString}` : ''}`,
        );

        // Log the user in
        devLog('info', 'sso login', 'logging in');
        login(email, password);
        return;
      }

      // If staff, get the login creds
      let keyPrefix = `CognitoIdentityServiceProvider.${import.meta.env.VITE_AWS_STAFF_USER_POOLS_WEB_CLIENT_ID}`;
      const cognitoLastAuthUser = creds[1];

      localStorage.setItem(`${keyPrefix}.LastAuthUser`, cognitoLastAuthUser);

      keyPrefix = `${keyPrefix}.${cognitoLastAuthUser}`;

      localStorage.setItem(`${keyPrefix}.accessToken`, creds[0]);
      localStorage.setItem(`${keyPrefix}.idToken`, creds[2]);
      localStorage.setItem(`${keyPrefix}.refreshToken`, creds[3]);
      localStorage.setItem(`mode`, `${staffLogin}`);

      params.delete('ssoToken');
      params.delete('mode');
      const paramsString = params.toString();

      setAuthSignedIn(true);
      setAuthLoading(false);

      window.history.pushState(
        {},
        '',
        `${window.location.origin}${!isEmpty(paramsString) ? `?${paramsString}` : ''}`,
      );
    }

    oldSsoSuccess.current = ssoCreds?.success;
  }, [ssoCreds?.success]);

  const mapErrorMessages = (message) => {
    if (message === 'Custom auth lambda trigger is not configured for the user pool.') {
      return 'Please enter your password';
    }
    return message;
  };

  const signUpConfig = {
    header: 'Sign Up to EatClub',
    hiddenDefaults: ['phone_number'],
  };

  const customTheme = {
    button: {
      backgroundColor: '#E54439',
      fontFamily: "'General Grotesque'",
      width: '100%',
      borderRadius: '4px',
    },
    a: {
      color: '#E54439',
    },
    navButton: {
      backgroundColor: '#E54439',
      fontFamily: "'General Grotesque'",
    },
    sectionFooter: {
      display: 'block',
    },
    sectionFooterPrimaryContent: {
      display: 'block',
      marginBottom: '12px',
    },
    toast: {
      backgroundColor: '#E54439',
    },
  };

  // On successful login, if a query param is present, redirect to that url
  useEffect(() => {
    const internalRedirect = params.get('redirectTo');

    if (!ssoToken && authSignedIn && !isEmpty(internalRedirect)) {
      devLog('info', 'Redirecting after login', internalRedirect);
      const params = new URLSearchParams(location.search);
      params.delete('redirectTo');

      // Note: This assumes the internal redirect begins with a /
      redirecting.current = true;
      window.history.replaceState({}, document.title, internalRedirect);
      history.push(internalRedirect);
    }
  }, [authSignedIn]);

  const staffRedirect = () => {
    if (!ssoToken && !redirecting.current) {
      console.log('staff redirect');
      redirecting.current = true;
      window.location.href = `${import.meta.env.VITE_LOGIN_URL}?callback_url=${
        window.location.origin
      }/staff${params.get('restId') ? `&restId=${params.get('restId')}` : ''}`;
    }
  };

  if (ssoCreds?.error) {
    if (staffLogin) {
      staffSignOut();
    } else {
      devLog('error', 'failed to switch account');
    }
  }

  return (
    <Authenticator
      onStateChange={(authState) => {
        if (authState === 'signedIn' && !ssoToken) {
          // NOTE: this will trigger when refreshing and logged in
          // show app here to prevent showing both login screen, and app
          devLog('debug', 'already signed in');
          setAuthSignedIn(true);
          setAuthLoading(false);
          return;
        }
        if (authState === 'signIn') {
          setAuthLoading(false);
        }
      }}
      hide={[Greetings, SignIn, SignUp, ForgotPassword, VerifyContact, Loading]}
      includeGreetings={false}
      amplifyConfig={(staffLogin || staffSession) && !partnerSession ? staffConfig : partnerConfig}
      theme={customTheme}
      signUpConfig={signUpConfig}
      usernameAttributes='email'
      errorMessage={mapErrorMessages}
    >
      <>
        {!ssoToken && authSignedIn && <App />}
        {!ssoToken && !authLoading && !authSignedIn && staffLogin && staffRedirect()}
        {!ssoToken && !authLoading && !authSignedIn && !staffLogin && (
          <CustomSignIn authSignedIn={authSignedIn} />
        )}
      </>
    </Authenticator>
  );
};

AppWithAuth.propTypes = {
  fetchSsoCreds: PropTypes.func.isRequired,
  setLogout: PropTypes.func.isRequired,
  ssoCreds: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    error: PropTypes.string,
    pending: PropTypes.bool,
    success: PropTypes.bool,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  ssoCreds: state.ssoCreds,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setLogout: setLogoutAction,
      fetchSsoCreds: fetchSsoCredsAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AppWithAuth);
